/* eslint-disable react-hooks/exhaustive-deps */
import { useMutation } from "@apollo/client";
import { Box, CircularProgress } from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import gql from "graphql-tag";
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { getCoupon } from "../../apollo/server";
import ConfigurationContext from "../../context/Configuration";
import UserContext from "../../context/User";
import AltNames from "../MenuName/AltNames";
import QtyStepper from "../RestaurantDetailComponent/Storefront/QtyStepper";
import { BUTTON_RESET, C, FONT_BODY, FONT_DISPLAY, FOCUS_RING, RADIUS, SHADOW, TYPE } from "../../utils/designTokens";
import { dealCondition, dealReward, dealThreshold } from "../../utils/dealText";
import { clearPendingPromo, readPendingPromo } from "../../utils/pendingPromo";
import { VariationModal } from "../Modals";
import { pushNestedSelectionLines } from "../../utils/nestedCartLines";
import { modifierOptionId } from "../../utils/modifierOptionId";
import { cateringItemIds, isCateringCart } from "../../utils/catering";
import { CateringBadge } from "../Catering/Catering";
import { RadioGroup, fieldSx, labelSx, metaSx, money, textButtonSx } from "./checkoutUi";
import { resizedImage } from "../../utils/resizedImage";

// Price a modifier option, honoring the group's duplicate pricing mode so the
// checkout total matches the customize modal (e.g. FIRST_FREE_THEN_CHARGED).
function computeOptionPrice(basePrice, qty, addon) {
  if (qty <= 0) return 0;
  const mode = addon?.allowDuplicates ? addon?.duplicatePricingMode : null;
  if (mode === "FIRST_FREE_THEN_CHARGED") {
    return basePrice * Math.max(0, qty - 1);
  }
  if ((mode === "INCREMENTAL" || mode === "CUSTOM") && addon?.duplicatePrices) {
    const tiers = typeof addon.duplicatePrices === "string"
      ? JSON.parse(addon.duplicatePrices) : addon.duplicatePrices;
    let total = 0;
    for (let i = 1; i <= qty; i++) {
      total += resolveTierPrice(tiers[String(i)], basePrice);
    }
    return total;
  }
  return basePrice * qty;
}

// Resolve a single Nth unit's price under an INCREMENTAL tier rule (relative
// to base): { mode: SAME|FREE|PLUS, extra }. Legacy bare numbers = absolute.
function resolveTierPrice(tier, basePrice) {
  if (tier === undefined || tier === null) return basePrice;
  if (typeof tier === "number") return tier;
  if (typeof tier === "string") {
    const n = parseFloat(tier);
    return Number.isNaN(n) ? basePrice : n;
  }
  const m = tier.mode || "SAME";
  if (m === "FREE") return 0;
  if (m === "PLUS") return basePrice + (parseFloat(tier.extra) || 0);
  if (m === "MINUS") return Math.max(0, basePrice - (parseFloat(tier.extra) || 0));
  return basePrice;
}

const GET_COUPON = gql`
  ${getCoupon}
`;

function CartItemsCard({
  restaurantData,
  cartRestaurant,
  setFlashMessage,
  selectedTip,
  setSelectedTip,
  tipDecided,
  customTipAmount,
  setCustomTipAmount,
  selectedDriverTip,
  setSelectedDriverTip,
  customDriverTipAmount,
  setCustomDriverTipAmount,
  setTaxValue,
  setCoupon,
  promoResult,
  selectedPromoId,
  setSelectedPromoId,
  calculatePrice,
  taxCalculation,
  calculateTip,
  calculateServerTip,
  calculateDriverTip,
  calculateServiceFee,
  calculateTotal,
  isPickUp,
  isSelfDelivery,
  deliveryCharges,
  memberDiscount,
  addQuantity,
  removeQuantity,
  onClearCart,
  setLoyaltyVoucherCode,
  loyaltyCouponCode,
  setLoyaltyCouponCode,
  // RMF Wallet credit paying part of this order (0 when the switch is off).
  walletApplied = 0,
  // Place order and the note under it, at the bottom of the card.
  footer = null,
  // Deals, rewards and tip options, fetched by checkout on arrival
  // (useSummaryData).
  summaryData = {},
  // True from the moment a deal or code is chosen until the server has priced
  // it, so the summary can say it is checking instead of looking stuck.
  promoPending = false,
}) {
  const couponRef = useRef(null);
  const [couponError, setCouponError] = useState(null);
  // The code a deal or reward was applied with (also what the voucher field
  // checks); voucherInput is just what is typed in the field.
  const [couponText, setCouponText] = useState("");
  const [voucherInput, setVoucherInput] = useState("");
  const { cart, deleteItem, updateCart } = useContext(UserContext);
  const configuration = useContext(ConfigurationContext);
  const currency = configuration?.currencySymbol || "$";
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [dealsExpanded, setDealsExpanded] = useState(false);
  const [rewardsExpanded, setRewardsExpanded] = useState(false);
  const [customTipOpen, setCustomTipOpen] = useState(selectedTip === "custom");

  const { dealsData, dealsLoading, loyaltyData, loyaltyCouponsData, dataTip } = summaryData;
  // Earned voucher for THIS restaurant only
  const earnedVoucher = (loyaltyData?.myLoyaltyVouchers || []).find(
    (v) => String(v.restaurantId) === String(cartRestaurant)
  ) || null;
  // Active points-redemption coupons from wallet
  const activeLoyaltyCoupons = (loyaltyCouponsData?.myLoyaltyCoupons || []).filter(
    c => c.status === 'ACTIVE' || c.status === 'REINSTATED'
  );

  // Catering deals (backend migration 131) apply to catering carts only, so a
  // regular cart doesn't offer them.
  const cateringIds = React.useMemo(() => cateringItemIds(restaurantData), [restaurantData]);
  const cateringCart = isCateringCart(cart, cateringIds);
  const availableDeals = (dealsData?.activePromotionsForCustomer || []).filter(
    (d) => !d.isCatering || cateringCart
  );

  // A deal chosen from anywhere — a code typed in the voucher box, an earned
  // reward — opens the offers list, so its progress and result are in view.
  useEffect(() => {
    if (selectedPromoId && availableDeals.some((d) => String(d._id) === String(selectedPromoId))) {
      setDealsExpanded(true);
    }
  }, [selectedPromoId]);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editAddonData, setEditAddonData] = useState(null);
  const pendingDeleteKeyRef = useRef(null);
  const [menuData, setMenuData] = useState({ foods: [], addons: [], options: [] });

  const toggleEditModal = useCallback(() => {
    setEditModalOpen((prev) => !prev);
  }, []);

  // Build menuData from restaurantData whenever it changes
  useEffect(() => {
    if (!restaurantData) return;

    const useAdvancedMenus = restaurantData?.advancedMenus?.some(
      (m) => m.isActive && m.items && m.items.length > 0
    );

    let foods = [];
    let addons = [];
    let options = [];

    if (useAdvancedMenus) {
      const addonsSet = new Set();
      const optionsSet = new Set();

      restaurantData.advancedMenus.forEach((menu) => {
        if (!menu.isActive) return;
        // Per-menu markup + menu-scoped id prefix — MUST mirror RestaurantDetail's
        // transform so the menu-scoped ids stored on the cart line match what we
        // rebuild here, and each menu carries its own marked-up component prices.
        const menuPct = parseFloat(menu.markupPercent || 0);
        const mk = (id) => `m${menu._id}_${id}`;
        const activeItems = (menu.items || []).filter(
          (i) => i.isActive && i.isAvailable !== false && !i.is86ed
        );
        activeItems.forEach((item) => {
          const variations = [];
          if (item.pricingMode === "VARIANTS" && item.variantPrices) {
            const entries = typeof item.variantPrices === "string"
              ? Object.entries(JSON.parse(item.variantPrices))
              : Object.entries(item.variantPrices);
            entries.forEach(([key, value]) => {
              variations.push({ _id: `${item._id}_${key}`, title: key, price: parseFloat(value) });
            });
          } else {
            variations.push({ _id: `${item._id}_default`, title: "Regular", price: parseFloat(item.basePrice || 0) });
          }
          // IDs of the addon/option groups this specific item is linked to, so
          // editing the item shows only ITS groups (not every group in the menu).
          const itemAddonGroupIds = [
            ...(item.modifierGroups || []).map((mg) => mk(mg._id.toString())),
            ...(item.optionGroups || []).map((og) => mk(`og_${og._id}`)),
          ];
          // Translated names travel with the line so checkout reads the same as
          // the menu the customer ordered from.
          foods.push({ _id: item._id.toString(), menuId: menu._id.toString(), markupPercent: menuPct, title: item.name, image: item.imageUrl || "", altName1: item.alternateName1 || null, altName2: item.alternateName2 || null, variations, addonGroupIds: itemAddonGroupIds });

          item.modifierGroups?.forEach((mg) => {
            const mgId = mk(mg._id.toString());
            const mgOptionIds = [];
            mg.modifiers?.forEach((gm) => {
              if (gm.isHidden) return;
              const mod = gm.modifier;
              if (!mod || mod.libraryStatus?.is86ed) return;
              const modId = modifierOptionId(mk, gm, mod._id, false);
              mgOptionIds.push(modId);
              if (!optionsSet.has(modId)) {
                optionsSet.add(modId);
                let price = parseFloat(gm.fixedPrice || 0);
                let variantPricesMap = null;
                if (gm.priceOverrideMode === "VARIANT_BASED" && gm.variantPrices) {
                  variantPricesMap = typeof gm.variantPrices === "string" ? JSON.parse(gm.variantPrices) : gm.variantPrices;
                  const vals = Object.values(variantPricesMap).map((v) => parseFloat(v));
                  if (vals.length > 0) price = Math.min(...vals);
                } else if (gm.priceOverrideMode === "FREE") {
                  price = 0;
                }
                options.push({ _id: modId, title: mod.name, price, variantPrices: variantPricesMap });
              }
            });
            if (!addonsSet.has(mgId)) {
              addonsSet.add(mgId);
              addons.push({
                _id: mgId, title: mg.name,
                quantityMinimum: mg.isRequired ? (mg.minSelections || 1) : 0,
                quantityMaximum: mg.maxSelections || mgOptionIds.length,
                allowDuplicates: mg.allowDuplicates || false,
                maxDuplicates: mg.maxDuplicates || null,
                duplicatePricingMode: mg.duplicatePricingMode || null,
                duplicatePrices: (mg.duplicatePricingMode === 'INCREMENTAL' || mg.duplicatePricingMode === 'CUSTOM') && mg.duplicatePrices
                  ? (typeof mg.duplicatePrices === 'string' ? JSON.parse(mg.duplicatePrices) : mg.duplicatePrices) : null,
                options: mgOptionIds,
                // Flat group cost, charged once when >=1 option in this group is picked.
                additionalCost: parseFloat(mg.additionalCost || 0),
              });
            }
          });

          item.optionGroups?.forEach((og) => {
            const ogId = mk(`og_${og._id}`);
            const ogOptionIds = [];
            og.choices?.forEach((choice, idx) => {
              const choiceId = mk(`og_${og._id}_choice_${idx}`);
              ogOptionIds.push(choiceId);
              if (!optionsSet.has(choiceId)) {
                optionsSet.add(choiceId);
                options.push({ _id: choiceId, title: choice, price: 0 });
              }
            });
            if (!addonsSet.has(ogId)) {
              addonsSet.add(ogId);
              addons.push({
                _id: ogId, title: og.name,
                quantityMinimum: og.isRequired ? 1 : 0,
                quantityMaximum: og.selectionType === "MULTI" ? (og.choices?.length || 99) : 1,
                options: ogOptionIds,
              });
            }
          });
        });
      });

      if (restaurantData.addons) addons = [...restaurantData.addons, ...addons];
      if (restaurantData.options) options = [...restaurantData.options, ...options];
    } else {
      foods = (restaurantData.categories || []).map((c) => c.foods.flat()).flat();
      addons = restaurantData.addons || [];
      options = restaurantData.options || [];
    }

    setMenuData({ foods, addons, options });
  }, [restaurantData]);

  // Enrich cart items with price/title whenever cart or menuData changes
  // This mirrors CartView.didFocus so Checkout always has correct prices
  useEffect(() => {
    if (!cart?.length || !menuData.foods.length) return;
    const { foods, addons, options } = menuData;
    const needsEnrichment = cart.some((item) => item.price === undefined || isNaN(parseFloat(item.price)));
    if (!needsEnrichment) return;

    // Track whether anything actually got enriched. If a re-enrichment pass
    // can't resolve an item (e.g. menu IDs shifted between renders) we keep
    // the original cart item rather than dropping it — dropping it would
    // empty the cart and wipe localStorage.
    let touched = false;
    const enriched = cart.map((cartItem) => {
      if (cartItem.price !== undefined && !isNaN(parseFloat(cartItem.price))) return cartItem;
      // Match the menu the line was ordered from FIRST — the same item id is on
      // several menus, each with its own marked-up price; matching by _id alone
      // returns the first menu's copy. Fall back to _id-only for legacy lines.
      const foodItem =
        foods.find((f) => f._id === cartItem._id && String(f.menuId) === String(cartItem.menuId))
        || foods.find((f) => f._id === cartItem._id);
      if (!foodItem) return cartItem; // keep as-is so the customer doesn't lose it
      const variationItem = foodItem.variations.find((v) => v._id === cartItem.variation._id);
      if (!variationItem) return cartItem;
      const foodItemTitle = `${foodItem.title}${variationItem.title ? `(${variationItem.title})` : ""}`;
      // Every price component already arrives marked up AND rounded per the menu's
      // rule from the backend, so the line is simply their sum — no re-rounding.
      let foodItemPrice = parseFloat(variationItem.price) || 0;
      let optionTitles = [];
      let optionsWithPrices = [];
      if (cartItem.addons) {
        cartItem.addons.forEach((addon) => {
          const cartAddon = addons.find((a) => a._id === addon._id);
          const groupFee = parseFloat(cartAddon?.additionalCost) || 0;
          if (groupFee > 0) foodItemPrice += groupFee;
          // Group header (name + flat fee) — mirrors the order summary layout.
          if ((addon.options || []).length > 0 || groupFee > 0) {
            const gName = cartAddon?.title || 'Group';
            optionTitles.push(gName);
            optionsWithPrices.push({ title: gName, price: groupFee > 0 ? groupFee.toFixed(2) : '', level: 0, header: true });
          }
          addon.options.forEach((option) => {
            // Item-as-modifier: price from the saved option (carries its own price
            // + nested selections); it isn't in the flattened `options` list.
            if (option.isItemMember) {
              const itemPrice = parseFloat(option.price) || 0;
              foodItemPrice += itemPrice;
              optionTitles.push(option.title);
              optionsWithPrices.push({ title: `+ ${option.title}`, price: itemPrice > 0 ? itemPrice.toFixed(2) : '', level: 1 });
              foodItemPrice += parseFloat(option.nested?.subtotal) || 0;
              pushNestedSelectionLines(option.nested?.childSelections, optionTitles, optionsWithPrices);
              return;
            }
            const cartOption = options.find((o) => o._id === option._id);
            if (!cartOption) return;
            const qty = parseInt(option._qty, 10) || 1;
            const basePrice = parseFloat(cartOption.price) || 0;
            const price = computeOptionPrice(basePrice, qty, cartAddon);
            foodItemPrice += Number.isFinite(price) ? price : 0;
            const dt = qty > 1 ? `${cartOption.title} (x${qty})` : cartOption.title;
            optionTitles.push(dt);
            optionsWithPrices.push({ title: `+ ${dt}`, price: (Number.isFinite(price) && price > 0) ? price.toFixed(2) : '', level: 1 });
            // A plain modifier can carry follow-up groups of its own.
            if (option.nested?.childSelections?.length) {
              // Charged per unit of this option, as the server does.
              foodItemPrice += (parseFloat(option.nested.subtotal) || 0) * qty;
              pushNestedSelectionLines(option.nested.childSelections, optionTitles, optionsWithPrices, 2, qty);
            }
          });
        });
      }
      touched = true;
      // Components are already rounded per-component (backend) — the line is
      // their sum; the bare variation is shown as-is.
      const safeLine = Number.isFinite(foodItemPrice) ? foodItemPrice : 0;
      return {
        ...cartItem,
        title: foodItemTitle,
        foodTitle: foodItem.title,
        foodAltName1: foodItem.altName1 || null,
        foodAltName2: foodItem.altName2 || null,
        variationTitle: variationItem.title,
        variationPrice: (parseFloat(variationItem.price) || 0).toFixed(2),
        optionTitles,
        optionsWithPrices,
        price: safeLine.toFixed(2),
      };
    });

    if (!touched) return;       // nothing changed; don't churn the cart
    updateCart(enriched);
  }, [cart, menuData]);

  const [mutate, { loading: couponLoading }] = useMutation(GET_COUPON, {
    onCompleted: couponCompleted,
    onError: couponOnError,
  });

  // A code already checked in the restaurant page's cart (utils/pendingPromo)
  // is applied here as if typed into the voucher box — once, on arrival.
  const autoApplyRef = useRef(false);
  useEffect(() => {
    if (!cartRestaurant) return;
    const pending = readPendingPromo(cartRestaurant);
    if (!pending) return;
    clearPendingPromo();
    autoApplyRef.current = true;
    setCouponText(pending.code);
    mutate({ variables: { coupon: pending.code, restaurantId: cartRestaurant } });
  }, [cartRestaurant]);

  useEffect(() => {
    const tip = dataTip?.tips?.[0];
    // `tipDecided` means a choice was restored for this cart — including a tip
    // the customer removed, which must not spring back to the default pill.
    if (tip && !selectedTip && !tipDecided) {
      // Default to the 20% pill (or the closest available variation) instead
      // of always picking the second variation.
      const variations = tip.tipVariations || [];
      const TARGET = 20;
      let best = variations[0];
      let bestDist = Math.abs(parseFloat(best) - TARGET);
      for (const v of variations) {
        const d = Math.abs(parseFloat(v) - TARGET);
        if (d < bestDist) { best = v; bestDist = d; }
      }
      setSelectedTip(best);
    }
  }, [restaurantData, dataTip]);

  useEffect(() => {
    setTaxValue(restaurantData ? +restaurantData.tax : 0);
  }, [restaurantData]);

  const mutateVoucher = (e) => {
    e.preventDefault();
    const code = voucherInput.trim();
    if (!code || couponLoading) return;
    setCouponError("");
    setCouponText(code);
    // Send the cart's restaurant so the server can reject spend vouchers
    // tied to a different restaurant with a clear message.
    mutate({ variables: { coupon: code, restaurantId: cartRestaurant || null } });
  };

  /**
   * Route the resolved coupon into the right cart slot based on its kind:
   *   POINT_COUPON  → loyaltyCouponCode (acts like a generic $-off voucher)
   *   SPEND_VOUCHER → selectedPromoId + loyaltyVoucherCode (restaurant deal)
   *   LEGACY        → setCoupon (existing % off path)
   */
  function couponCompleted({ coupon }) {
    autoApplyRef.current = false;
    if (!coupon || coupon.enabled === false) {
      setCouponError("Coupon not found!");
      setFlashMessage({ type: "warning", message: "Coupon unavailable." });
      return;
    }
    couponRef.current = coupon;
    setCouponError("");

    const kind = coupon.kind || 'LEGACY';

    if (kind === 'POINT_COUPON') {
      if (setLoyaltyCouponCode) {
        setLoyaltyCouponCode(coupon.title, coupon.discountValue);
      }
      // Don't let a previously-set legacy/promo discount linger.
      setSelectedPromoId && setSelectedPromoId(null);
      setCoupon({});
      setIsCouponApplied(false);
    } else if (kind === 'SPEND_VOUCHER') {
      // Spend vouchers are wired through the promotion engine via the
      // matching promotion's id + voucher code. Both have to be set.
      if (setSelectedPromoId) setSelectedPromoId(coupon.promotionId || null);
      if (setLoyaltyVoucherCode) setLoyaltyVoucherCode(coupon.title);
      setCoupon({});
      setIsCouponApplied(false);
    } else {
      // Legacy / promo coupon — existing flow.
      setCoupon(coupon);
      setIsCouponApplied(true);
    }

    setFlashMessage({ type: "success", message: "Coupon applied." });
    setVoucherInput("");
  }
  function couponOnError(error) {
    // Surface the server's exact message ("This voucher only works at …",
    // "expired", "already used", …) so the customer knows what went wrong.
    const raw = error?.message || '';
    const msg = raw.replace(/^GraphQL error:\s*/i, '').trim() || 'Invalid coupon';
    autoApplyRef.current = false;
    setCouponError(msg);
    setFlashMessage({ type: "error", message: msg });
  }

  // ── Money ───────────────────────────────────────────────────────────────
  // Split the item discount between the deal (or legacy coupon) and a reward
  // coupon the same way calculatePrice applies them, so the rows add up to the
  // total exactly.
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const gross = +calculatePrice(0, false);
  const itemsNet = +calculatePrice(0, true);
  const itemsOff = round2(Math.max(0, gross - itemsNet));
  const dealItemsOff = round2(Math.min(
    itemsOff,
    promoResult?.discountAmount > 0
      ? promoResult.discountAmount
      : isCouponApplied && couponRef.current?.discount
        ? (couponRef.current.discount / 100) * gross
        : 0
  ));
  const rewardOff = round2(itemsOff - dealItemsOff);
  const deliveryShown = isPickUp ? 0 : parseFloat(deliveryCharges || 0);
  const deliveryNet = isPickUp ? 0 : round2(+calculatePrice(deliveryShown, true) - itemsNet);
  const deliveryOff = round2(Math.max(0, deliveryShown - deliveryNet));
  const dealOff = round2(dealItemsOff + deliveryOff);
  const serviceFee = calculateServiceFee ? calculateServiceFee() : 0;
  const tipAmount = +calculateTip();
  const total = +calculateTotal();
  const due = round2(Math.max(0, total - walletApplied));
  const dealLabel = promoResult?.appliedPromotion
    ? promoResult.appliedPromotion.name || "Deal"
    : isCouponApplied ? `Coupon ${couponText}` : "Deal";
  const appliedReward = loyaltyCouponCode ? activeLoyaltyCoupons.find((c) => c.code === loyaltyCouponCode) : null;

  // ── Pieces ──────────────────────────────────────────────────────────────
  const clearDeal = () => {
    setSelectedPromoId(null);
    if (setLoyaltyVoucherCode) setLoyaltyVoucherCode(null);
    setCoupon({});
    setCouponText("");
    setIsCouponApplied(false);
    couponRef.current = null;
  };

  const smallButtonSx = {
    ...BUTTON_RESET,
    position: "relative",
    flexShrink: 0,
    height: 36,
    px: 1.5,
    borderRadius: RADIUS.control,
    border: `1px solid ${C.ink}`,
    bgcolor: C.bg,
    fontFamily: FONT_BODY,
    fontSize: 13,
    fontWeight: 700,
    color: C.ink,
    "&::before": { content: '""', position: "absolute", inset: "-4px 0" },
    "&:hover": { bgcolor: C.surface },
    ...FOCUS_RING,
  };

  const toggleRowSx = (tone) => ({
    ...BUTTON_RESET,
    width: "100%",
    minHeight: 48,
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 1.5,
    boxSizing: "border-box",
    borderRadius: RADIUS.control,
    bgcolor: tone === "green" ? C.greenTint : C.goldSoft,
    border: `1px solid ${tone === "green" ? C.greenSoft : "#F3E2BD"}`,
    color: tone === "green" ? C.green : C.gold,
    fontFamily: FONT_BODY,
    ...FOCUS_RING,
  });

  const toggleRow = ({ tone, Icon, label, open, onToggle, controls }) => (
    <Box component="button" type="button" aria-expanded={open} aria-controls={controls} onClick={onToggle} sx={toggleRowSx(tone)}>
      <Icon aria-hidden="true" sx={{ fontSize: 20 }} />
      <Box component="span" sx={{ flex: 1, textAlign: "left", fontSize: 14, fontWeight: 700 }}>{label}</Box>
      <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{open ? "Hide" : "View"}</Box>
      <ExpandMoreRoundedIcon aria-hidden="true" sx={{ fontSize: 20, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s ease" }} />
    </Box>
  );

  const progress = (at, label) => {
    const unlocked = gross >= at;
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ ...metaSx, fontWeight: 700, color: unlocked ? C.green : C.gold, display: "flex", alignItems: "center", gap: 0.5 }}>
          {unlocked && <CheckCircleRoundedIcon aria-hidden="true" sx={{ fontSize: 16 }} />}
          {unlocked ? "Unlocked" : `Add ${money(at - gross, currency)} more to unlock`}
        </Box>
        <Box
          role="progressbar"
          aria-label={`Progress to ${label}`}
          aria-valuemin={0}
          aria-valuemax={round2(at)}
          aria-valuenow={round2(Math.min(gross, at))}
          sx={{ mt: 0.75, height: 8, borderRadius: RADIUS.pill, bgcolor: "rgba(138,90,0,0.14)", overflow: "hidden" }}
        >
          <Box sx={{ height: "100%", width: `${Math.max(4, Math.min(100, (gross / at) * 100))}%`, bgcolor: unlocked ? C.green : C.goldAccent, borderRadius: RADIUS.pill, transition: "width .3s ease" }} />
        </Box>
      </Box>
    );
  };

  const savedLine = (text) => (
    <Box sx={{ ...metaSx, mt: 1, display: "flex", alignItems: "center", gap: 0.5, color: C.green, fontWeight: 600 }}>
      <CheckCircleRoundedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
      {text}
    </Box>
  );

  const youSave = `You save ${money(dealOff, currency)}`;

  const dealBox = (deal) => {
    // "Selected" = Apply was pressed; "applied" = the server accepted it. A
    // selected-but-rejected offer (say, under the minimum) must not look applied.
    const isSelected = selectedPromoId === deal._id;
    // Chosen and still being priced: say so, and don't let the previous
    // answer (another deal's "Not eligible", say) stand in for this one.
    const checking = isSelected && promoPending;
    const isApplied = !checking && isSelected && String(promoResult?.appliedPromotion?._id) === String(deal._id);
    const isRejected = !checking && isSelected && !isApplied && !!promoResult?.rejectionMessage;
    const isLoyalty = deal.promotionType === "LOYALTY_REWARD";
    const at = dealThreshold(deal);
    const terms = [dealReward(deal), dealCondition(deal)].filter(Boolean).join(" ");
    return (
      <Box key={deal._id} sx={{ p: 1.5, borderRadius: RADIUS.control, bgcolor: C.goldSoft }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, color: C.ink }}>{deal.name}</Box>
            {terms && <Box sx={{ ...metaSx, color: C.gold }}>{terms}</Box>}
            {deal.isCatering && <CateringBadge label="Catering deal" sx={{ mt: 0.5 }} />}
          </Box>
          {isLoyalty ? (
            <Box sx={{ ...metaSx, color: C.gold, fontWeight: 600, flexShrink: 0 }}>Apply once earned</Box>
          ) : checking ? (
            <Box
              component="button"
              type="button"
              disabled
              aria-label={`Applying ${deal.name}`}
              sx={{ ...smallButtonSx, display: "inline-flex", alignItems: "center", gap: 0.75, cursor: "progress", "&:hover": {} }}
            >
              <CircularProgress size={14} thickness={5} sx={{ color: C.ink }} />
              Applying
            </Box>
          ) : isApplied ? (
            <Box component="button" type="button" onClick={clearDeal} aria-label={`Remove ${deal.name}`} sx={{ ...textButtonSx, fontSize: 13, flexShrink: 0 }}>
              Remove
            </Box>
          ) : isRejected ? (
            <Box sx={{ ...metaSx, flexShrink: 0 }}>Not eligible</Box>
          ) : (
            <Box
              component="button"
              type="button"
              aria-label={`Apply ${deal.name}`}
              onClick={() => {
                setSelectedPromoId(deal._id);
                // A code typed earlier belongs to another deal.
                if (setLoyaltyVoucherCode) setLoyaltyVoucherCode(null);
                setCoupon({});
                setIsCouponApplied(false);
              }}
              sx={smallButtonSx}
            >
              Apply
            </Box>
          )}
        </Box>
        {at && progress(at, deal.name)}
        {checking && (
          <Box role="status" sx={{ ...metaSx, mt: 1, color: C.gold, fontWeight: 600 }}>Checking this offer…</Box>
        )}
        {isApplied && savedLine(`${promoResult.explanationMessage || "Applied"} · ${youSave}`)}
        {isRejected && (
          <Box role="alert" sx={{ mt: 1, display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ ...metaSx, flex: 1, color: C.redLink }}>{promoResult.rejectionMessage}</Box>
            <Box component="button" type="button" onClick={clearDeal} sx={{ ...textButtonSx, fontSize: 13 }}>Dismiss</Box>
          </Box>
        )}
      </Box>
    );
  };

  const rewardRow = ({ key, title, code, expiresAt, applied, onApply, onRemove }) => {
    const daysLeft = expiresAt ? Math.ceil((new Date(expiresAt) - new Date()) / 86400000) : null;
    return (
      <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, borderRadius: RADIUS.control, bgcolor: C.goldSoft }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, color: C.ink }}>{title}</Box>
          <Box sx={{ ...metaSx, color: C.gold }}>
            {[code, daysLeft !== null ? (daysLeft > 0 ? `Expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}` : "Expires today") : null].filter(Boolean).join(" · ")}
          </Box>
        </Box>
        {applied ? (
          <Box component="button" type="button" onClick={onRemove} aria-label={`Remove ${title}`} sx={{ ...textButtonSx, fontSize: 13, flexShrink: 0 }}>Remove</Box>
        ) : (
          <Box component="button" type="button" onClick={onApply} aria-label={`Apply ${title}`} sx={smallButtonSx}>Apply</Box>
        )}
      </Box>
    );
  };

  const rewardCount = (earnedVoucher ? 1 : 0) + activeLoyaltyCoupons.length;
  const loyaltyPromo = availableDeals.find((d) => d.promotionType === "LOYALTY_REWARD");
  const earnedApplied = !!(earnedVoucher && loyaltyPromo && selectedPromoId === loyaltyPromo._id && couponText === earnedVoucher.voucherCode);
  const codeDealApplied = promoResult?.appliedPromotion &&
    !availableDeals.some((d) => String(d._id) === String(promoResult.appliedPromotion._id));

  // ── Tip ─────────────────────────────────────────────────────────────────
  const tipVariations = dataTip?.tips?.[0]?.tipVariations || [];
  const tipFor = (pct) => round2((gross / 100) * parseFloat(pct));
  const tipButtonSx = (checked) => ({
    ...BUTTON_RESET,
    minHeight: 56,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.25,
    boxSizing: "border-box",
    borderRadius: RADIUS.control,
    border: `1px solid ${checked ? C.red : C.borderStrong}`,
    boxShadow: checked ? `inset 0 0 0 1px ${C.red}` : "none",
    bgcolor: checked ? C.redTint : C.bg,
    fontFamily: FONT_BODY,
    color: C.ink,
    "&:hover": checked ? {} : { borderColor: C.ink },
    ...FOCUS_RING,
  });
  const tipButton = (key, label, amount, checked, onSelect) => (
    <Box
      key={key}
      component="button"
      type="button"
      role="radio"
      aria-checked={checked ? "true" : "false"}
      tabIndex={checked ? 0 : -1}
      onClick={onSelect}
      sx={tipButtonSx(checked)}
    >
      <Box component="span" sx={{ fontSize: 14, fontWeight: 700 }}>{label}</Box>
      <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: C.muted, fontVariantNumeric: "tabular-nums" }}>{amount}</Box>
    </Box>
  );

  // ── Rows ────────────────────────────────────────────────────────────────
  const row = (label, value, { tone, bold } = {}) => (
    <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5 }}>
      <Box component="dt" sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: bold ? 700 : 400, color: tone === "green" ? C.green : C.ink }}>{label}</Box>
      <Box component="dd" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 14, fontWeight: bold ? 700 : 500, color: tone === "green" ? C.green : C.ink, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{value}</Box>
    </Box>
  );

  const thumb = restaurantData?.logo || restaurantData?.image;

  return (
    <>
      <Box
        component="section"
        aria-labelledby="checkout-summary-title"
        sx={{
          borderRadius: RADIUS.card,
          border: `1px solid ${C.border}`,
          bgcolor: C.bg,
          boxShadow: SHADOW.float,
          overflow: "hidden",
        }}
      >
        {/* Whose order this is */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: { xs: 2, sm: 3 }, py: 2, borderBottom: `1px solid ${C.border}` }}>
          {thumb ? (
            <Box component="img" src={resizedImage(thumb, 128)} alt="" sx={{ width: 48, height: 48, borderRadius: RADIUS.control, objectFit: "cover", flexShrink: 0, bgcolor: C.surface2 }} />
          ) : (
            <Box aria-hidden="true" sx={{ width: 48, height: 48, borderRadius: RADIUS.control, flexShrink: 0, bgcolor: C.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, color: C.ink }}>
              {(restaurantData?.name || "R").charAt(0)}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={metaSx}>Your order from</Box>
            <Box component="h2" id="checkout-summary-title" sx={{ ...TYPE.subHeading, m: 0, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {restaurantData?.name}
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => {
              if (window.confirm("Clear your order?")) onClearCart();
            }}
            aria-label="Clear your order"
            sx={textButtonSx}
          >
            Clear
          </Box>
        </Box>

        {/* Lines, offers, voucher, tip and the breakdown */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Box component="ul" aria-label="Items in your order" sx={{ m: 0, p: 0 }}>
            {cart?.map((item) => {
              const food =
                menuData.foods.find((f) => f._id === item._id && String(f.menuId) === String(item.menuId))
                || menuData.foods.find((f) => f._id === item._id);
              const name = item.foodTitle || item.title || food?.title || "Item";
              const size = item.variationTitle && item.variationTitle !== "Regular" ? item.variationTitle : null;
              const addOns = item.optionsWithPrices?.length
                ? item.optionsWithPrices
                    .filter((o) => !o.header)
                    .map((o) => {
                      const title = String(o.title || "").replace(/^[+•]\s*/, "");
                      return o.price ? `${title} (+${currency}${o.price})` : title;
                    })
                : item.optionTitles || [];
              const details = [size, ...addOns].filter(Boolean).join(" · ");
              const image = food?.image;
              return (
                <Box component="li" key={item.key} sx={{ listStyle: "none", display: "flex", gap: 1.5, py: 2, borderBottom: `1px solid ${C.border}` }}>
                  {image ? (
                    <Box component="img" src={resizedImage(image, 128)} alt="" loading="lazy" sx={{ width: 56, height: 56, borderRadius: RADIUS.control, objectFit: "cover", flexShrink: 0, bgcolor: C.surface2 }} />
                  ) : (
                    <Box aria-hidden="true" sx={{ width: 56, height: 56, borderRadius: RADIUS.control, flexShrink: 0, bgcolor: C.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 20, color: C.muted }}>
                      {name.charAt(0)}
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                      <Box sx={{ ...TYPE.itemName, color: C.ink, wordBreak: "break-word" }}>
                        {name}
                        <AltNames alt1={item.foodAltName1} alt2={item.foodAltName2} displayMode={restaurantData?.menuLanguageDisplay || "NONE"} />
                      </Box>
                      <Box sx={{ ...TYPE.itemName, color: C.ink, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                        {money(parseFloat(item.price || 0) * item.quantity, currency)}
                      </Box>
                    </Box>
                    {details && <Box sx={{ ...metaSx, mt: 0.25, wordBreak: "break-word" }}>{details}</Box>}
                    {item.specialInstructions && (
                      <Box sx={{ ...metaSx, mt: 0.5, color: C.ink }}>{`Note: ${item.specialInstructions}`}</Box>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                      {food ? (
                        <Box
                          component="button"
                          type="button"
                          aria-label={`Edit ${name}`}
                          sx={{ ...textButtonSx, fontSize: 13 }}
                          onClick={() => {
                            pendingDeleteKeyRef.current = item.key;
                            // Only the groups this food is linked to, otherwise the
                            // modal renders every group in the menu.
                            const foodAddons = Array.isArray(food.addonGroupIds)
                              ? menuData.addons.filter((a) => food.addonGroupIds.includes(a._id))
                              : menuData.addons;
                            setEditAddonData({
                              food,
                              addons: foodAddons,
                              options: menuData.options,
                              restaurant: restaurantData._id,
                              image: restaurantData.image,
                              initialCartItem: {
                                variation: item.variation,
                                addons: item.addons,
                                quantity: item.quantity,
                                specialInstructions: item.specialInstructions,
                              },
                            });
                            setEditModalOpen(true);
                          }}
                        >
                          Edit
                        </Box>
                      ) : <span />}
                      <QtyStepper
                        quantity={item.quantity}
                        itemName={name}
                        redAdd
                        onAdd={() => addQuantity(item.key)}
                        onRemove={() => {
                          if (item.quantity > 1) removeQuantity(item.key);
                          else deleteItem(item.key);
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Offers: the deals list, each with its progress to the minimum */}
          {dealsLoading && availableDeals.length === 0 && (
            <Box role="status" sx={{ ...toggleRowSx("green"), mt: 2.5, cursor: "default", gap: 1.25 }}>
              <CircularProgress size={16} thickness={5} sx={{ color: C.green }} />
              <Box component="span" sx={{ fontSize: 14, fontWeight: 600 }}>Checking offers…</Box>
            </Box>
          )}
          {(availableDeals.length > 0 || codeDealApplied) && (
            <Box sx={{ mt: 2.5 }}>
              {codeDealApplied && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, mb: 1, borderRadius: RADIUS.control, bgcolor: C.greenSoft }}>
                  <CheckCircleRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: C.green }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, color: C.green }}>
                      {promoResult.explanationMessage || `${promoResult.appliedPromotion.name} applied`}
                    </Box>
                    <Box sx={{ ...metaSx, color: C.green }}>{youSave}</Box>
                  </Box>
                  <Box component="button" type="button" onClick={clearDeal} sx={{ ...textButtonSx, fontSize: 13 }}>Remove</Box>
                </Box>
              )}
              {availableDeals.length > 0 && (
                <>
                  {toggleRow({
                    tone: "green",
                    Icon: LocalOfferOutlinedIcon,
                    label: `${availableDeals.length} offer${availableDeals.length > 1 ? "s" : ""} available`,
                    open: dealsExpanded,
                    onToggle: () => setDealsExpanded((v) => !v),
                    controls: "checkout-offers",
                  })}
                  <Box id="checkout-offers" sx={{ display: dealsExpanded ? "flex" : "none", flexDirection: "column", gap: 1, mt: 1 }}>
                    {availableDeals.map(dealBox)}
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Rewards: an earned loyalty voucher and points coupons */}
          {rewardCount > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {toggleRow({
                tone: "gold",
                Icon: CardGiftcardOutlinedIcon,
                label: `${rewardCount} reward${rewardCount > 1 ? "s" : ""} & coupon${rewardCount > 1 ? "s" : ""}`,
                open: rewardsExpanded,
                onToggle: () => setRewardsExpanded((v) => !v),
                controls: "checkout-rewards",
              })}
              <Box id="checkout-rewards" sx={{ display: rewardsExpanded ? "flex" : "none", flexDirection: "column", gap: 1, mt: 1 }}>
                {earnedVoucher && rewardRow({
                  key: "earned",
                  title: `Your reward: ${earnedVoucher.voucherAmount ? `${money(earnedVoucher.voucherAmount, currency)} off` : "Loyalty voucher"}`,
                  code: earnedVoucher.voucherCode,
                  expiresAt: earnedVoucher.voucherExpiresAt,
                  applied: earnedApplied,
                  onApply: () => {
                    setCouponText(earnedVoucher.voucherCode);
                    if (setLoyaltyVoucherCode) setLoyaltyVoucherCode(earnedVoucher.voucherCode);
                    setSelectedPromoId(loyaltyPromo?._id || null);
                  },
                  onRemove: () => {
                    setSelectedPromoId(null);
                    if (setLoyaltyVoucherCode) setLoyaltyVoucherCode(null);
                    setCouponText("");
                  },
                })}
                {activeLoyaltyCoupons.map((lc) => rewardRow({
                  key: lc._id,
                  title: `${money(lc.discountValue, currency)} off`,
                  code: lc.code,
                  expiresAt: lc.expiresAt,
                  applied: loyaltyCouponCode === lc.code,
                  onApply: () => setLoyaltyCouponCode && setLoyaltyCouponCode(lc.code, lc.discountValue),
                  onRemove: () => setLoyaltyCouponCode && setLoyaltyCouponCode(null, 0),
                }))}
              </Box>
            </Box>
          )}

          {/* Voucher code */}
          {isCouponApplied ? (
            <Box role="status" sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, px: 1.5, minHeight: 48, borderRadius: RADIUS.control, bgcolor: C.greenSoft }}>
              <CheckCircleRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: C.green }} />
              <Box sx={{ ...metaSx, flex: 1, color: C.green, fontWeight: 600 }}>
                <strong>{couponText}</strong> applied
              </Box>
              <Box component="button" type="button" onClick={clearDeal} sx={{ ...textButtonSx, fontSize: 13 }}>Remove</Box>
            </Box>
          ) : (
            <Box component="form" onSubmit={mutateVoucher} noValidate sx={{ mt: 2 }}>
              <Box component="label" htmlFor="checkout-voucher" sx={{ ...labelSx, display: "block", mb: 0.75 }}>Voucher code</Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Box
                  component="input"
                  id="checkout-voucher"
                  type="text"
                  value={voucherInput}
                  onChange={(e) => setVoucherInput(e.target.value)}
                  placeholder="Enter code"
                  autoComplete="off"
                  autoCapitalize="characters"
                  aria-invalid={couponError ? "true" : undefined}
                  aria-describedby={couponError ? "checkout-voucher-error" : undefined}
                  sx={{ ...fieldSx, flex: 1, minWidth: 0, borderColor: couponError ? C.redLink : C.borderStrong }}
                />
                <Box
                  component="button"
                  type="submit"
                  disabled={couponLoading || !voucherInput.trim()}
                  sx={{
                    ...BUTTON_RESET,
                    height: 44,
                    px: 2,
                    flexShrink: 0,
                    borderRadius: RADIUS.control,
                    bgcolor: C.ink,
                    color: "#FFFFFF",
                    fontFamily: FONT_BODY,
                    fontSize: 14,
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#33312F" },
                    "&:disabled": { bgcolor: C.disabledBg, color: C.disabledText, cursor: "default" },
                    ...FOCUS_RING,
                  }}
                >
                  {couponLoading ? "Checking" : "Apply"}
                </Box>
              </Box>
              {couponError && (
                <Box id="checkout-voucher-error" role="alert" sx={{ ...metaSx, color: C.redLink, mt: 0.75 }}>{couponError}</Box>
              )}
            </Box>
          )}

          {/* Tip */}
          {tipVariations.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.25 }}>
                <Box id="checkout-tip-label" sx={{ ...TYPE.subHeading, color: C.ink }}>
                  {isPickUp ? "Tip the restaurant" : "Tip your driver"}
                </Box>
                <Box component="span" sx={metaSx}>Optional</Box>
              </Box>
              <RadioGroup
                labelledBy="checkout-tip-label"
                sx={{ display: "grid", gridTemplateColumns: `repeat(${tipVariations.length + 1}, minmax(0, 1fr))`, gap: 1 }}
              >
                {tipVariations.map((pct) =>
                  tipButton(String(pct), `${pct}%`, money(tipFor(pct), currency), selectedTip === pct, () => {
                    setSelectedTip(pct);
                    setCustomTipAmount("");
                    setCustomTipOpen(false);
                  })
                )}
                {tipButton("none", "None", money(0, currency), selectedTip === null, () => {
                  setSelectedTip(null);
                  setCustomTipAmount("");
                  setCustomTipOpen(false);
                })}
              </RadioGroup>
              {customTipOpen || selectedTip === "custom" ? (
                <Box sx={{ mt: 1.25 }}>
                  <Box component="label" htmlFor="checkout-custom-tip" sx={{ ...labelSx, display: "block", mb: 0.75 }}>Other tip amount</Box>
                  <Box sx={{ position: "relative" }}>
                    <Box component="span" aria-hidden="true" sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontFamily: FONT_BODY, fontSize: 15, color: C.muted }}>{currency}</Box>
                    <Box
                      component="input"
                      id="checkout-custom-tip"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={customTipAmount}
                      onChange={(e) => {
                        setSelectedTip("custom");
                        setCustomTipAmount(e.target.value);
                      }}
                      placeholder="0.00"
                      sx={{ ...fieldSx, pl: 3.5 }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setCustomTipOpen(true)}
                  sx={{ ...textButtonSx, fontSize: 13, mt: 1.25 }}
                >
                  Other amount
                </Box>
              )}
            </Box>
          )}

          {/* Price breakdown */}
          <Box component="dl" aria-label="Price breakdown" sx={{ m: 0, mt: 2.5, pt: 2, borderTop: `1px solid ${C.border}` }}>
            {row("Subtotal", money(gross, currency))}
            {dealOff > 0 && row(dealLabel, `-${money(dealOff, currency)}`, { tone: "green" })}
            {rewardOff > 0 && row(appliedReward ? `Reward coupon ${appliedReward.code}` : "Reward coupon", `-${money(rewardOff, currency)}`, { tone: "green" })}
            {row("Delivery fee", isPickUp || deliveryShown === 0 ? "Free" : money(deliveryShown, currency))}
            {!isPickUp && memberDiscount > 0 && row("Member discount", `-${money(memberDiscount, currency)}`, { tone: "green" })}
            {serviceFee > 0 && row("Service fee", money(serviceFee, currency))}
            {row("Tax", money(taxCalculation(), currency))}
            {row("Tip", money(tipAmount, currency))}
            {walletApplied > 0 && row("RMF Wallet", `-${money(walletApplied, currency)}`, { tone: "green" })}
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, pb: { xs: 2, sm: 3 }, borderTop: `1px solid ${C.border}` }}>
          <Box component="dl" sx={{ m: 0, mb: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2 }}>
              <Box component="dt" sx={{ fontFamily: FONT_BODY, fontSize: 16, fontWeight: 700, color: C.ink }}>
                {walletApplied > 0 ? "Due now" : "Total"}
              </Box>
              <Box component="dd" aria-live="polite" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 18, fontWeight: 700, color: C.ink, fontVariantNumeric: "tabular-nums" }}>
                {money(due, currency)}
              </Box>
            </Box>
          </Box>
          {footer}
        </Box>
      </Box>
      <VariationModal
        isVisible={editModalOpen}
        toggleModal={toggleEditModal}
        data={editAddonData}
        langDisplay={restaurantData?.menuLanguageDisplay || 'NONE'}
        replaceKey={pendingDeleteKeyRef.current}
      />
    </>
  );
}

export default React.memo(CartItemsCard);
